import { useOutletContext } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import { COLORS, FONTS } from '../../styles/tokens';
import type { ColorTheme } from '../../styles/tokens';
import { AppIcon } from '../../components/AppIcon';
import type { AppDetailContext } from './app-detail-context';

/** App overview sub-page - read-only info display */
export default function AppOverview(): React.JSX.Element {
  const { client, userCount } = useOutletContext<AppDetailContext>();
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;

  const attrs = client.attributes ?? {};
  const launchUrl = client.rootUrl || client.baseUrl || '';
  const team = attrs['vertex.team'] ?? 'Not specified';
  const category = attrs['vertex.category'] ?? 'Uncategorized';
  const status = client.enabled ? 'Active' : 'Disabled';

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
      <InfoCard title="App Info" isDark={isDark} colors={colors}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
          <AppIcon
            icon={attrs['vertex.icon'] ?? ''}
            iconDark={attrs['vertex.icon.dark']}
            name={client.name || client.clientId}
            isDark={isDark}
            size={48}
          />
        </div>
        <InfoRow label="Name" value={client.name || client.clientId} colors={colors} />
        <InfoRow label="Client ID" value={client.clientId} colors={colors} mono />
        <InfoRow label="Description" value={client.description ?? 'No description'} colors={colors} />
        <InfoRow label="Launch URL" value={launchUrl || 'Not configured'} colors={colors} mono />
      </InfoCard>

      <InfoCard title="Details" isDark={isDark} colors={colors}>
        <StatusRow label="Status" status={status} />
        <InfoRow label="Team" value={team} colors={colors} />
        <InfoRow label="Category" value={category} colors={colors} />
        <InfoRow label="Users" value={String(userCount)} colors={colors} />
        <InfoRow label="Public Client" value={client.publicClient ? 'Yes' : 'No'} colors={colors} />
      </InfoCard>
    </div>
  );
}

/** Info card container */
function InfoCard({ title, isDark, colors, children }: {
  title: string; isDark: boolean; colors: ColorTheme; children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="vx-fill-col" style={{
      flex: '1 1 320px', minWidth: '280px', padding: '20px', borderRadius: '10px',
      border: `1px solid ${colors.border}`,
      backgroundColor: isDark ? COLORS.dark.card : COLORS.light.card,
    }}>
      <h3 style={{
        fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.15em',
        textTransform: 'uppercase', color: colors.textSecondary, margin: '0 0 16px',
      }}>
        {title}
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {children}
      </div>
    </div>
  );
}


/** Single info row */
function InfoRow({ label, value, colors, mono }: {
  label: string; value: string; colors: ColorTheme; mono?: boolean;
}): React.JSX.Element {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
      <span style={{ fontFamily: FONTS.body, fontSize: '12px', color: colors.textSecondary, flexShrink: 0 }}>
        {label}
      </span>
      <span style={{
        fontFamily: mono ? 'monospace' : FONTS.body, fontSize: '12px',
        color: colors.text, textAlign: 'right', wordBreak: 'break-all',
      }}>
        {value}
      </span>
    </div>
  );
}

/** Status row with colored indicator */
function StatusRow({ label, status }: { label: string; status: string }): React.JSX.Element {
  const isActive = status === 'Active';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontFamily: FONTS.body, fontSize: '12px', color: '#888' }}>{label}</span>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: '2px 10px', borderRadius: '12px',
        backgroundColor: isActive ? 'rgba(74, 222, 128, 0.15)' : 'rgba(239, 68, 68, 0.15)',
        fontFamily: FONTS.body, fontSize: '11px', fontWeight: 600,
        color: isActive ? '#4ADE80' : '#EF4444',
      }}>
        <span style={{
          width: '6px', height: '6px', borderRadius: '50%',
          backgroundColor: isActive ? '#4ADE80' : '#EF4444',
        }} />
        {status}
      </span>
    </div>
  );
}
